import { AlertTriangle, RefreshCw, WifiOff } from 'lucide-react'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { resolveErrorMessage } from '@/lib/error-messages'
import { flattenFieldErrors, type ApiError } from '@/lib/problem-details'
import { cn } from '@/lib/utils'

/**
 * The application's two error surfaces.
 *
 * Both read the same normalised {@link ApiError}: the RFC 9457 problem the API returned, reduced
 * to a stable dotted code plus its details. The friendly wording is looked up from the code, so
 * the server is free to reword its own messages without changing what an operator reads, and an
 * unmapped code still produces something usable rather than a blank panel.
 *
 * The code and correlation id are always shown, small. When a teller telephones support, those
 * two strings are what turn "it did not work" into a specific request in the audit trail.
 */

const isDenied = (error: ApiError) => error.status === 403

export interface ErrorSurfaceProps {
  error: ApiError
  onRetry?: () => void
  /** Overrides the heading; the resolved message still renders beneath it. */
  title?: string
  className?: string
}

/** Block-level failure state, for a panel or table that could not load. */
export function ErrorSurface({ error, onRetry, title, className }: ErrorSurfaceProps) {
  const Icon = error.isNetworkFailure ? WifiOff : AlertTriangle

  return (
    <div
      className={cn(
        'flex flex-col items-center gap-3 rounded-card border border-border-subtle bg-surface px-4 py-10 text-center',
        className,
      )}
      role="alert"
    >
      <Icon className={cn('size-6', isDenied(error) ? 'text-warning-text' : 'text-danger-text')} aria-hidden />
      <div className="space-y-1">
        <p className="text-sm font-semibold text-heading">{title ?? error.title}</p>
        <p className="mx-auto max-w-lg text-xs text-body">{resolveErrorMessage(error)}</p>
      </div>

      <ErrorReference error={error} />

      {onRetry && !isDenied(error) ? (
        <Button variant="secondary" size="sm" onClick={onRetry}>
          <RefreshCw />
          Try again
        </Button>
      ) : null}
    </div>
  )
}

/** Inline failure state, for the top of a form after a rejected submission. */
export function ProblemAlert({ error, className }: { error: ApiError; className?: string }) {
  const fieldErrors = flattenFieldErrors(error.fieldErrors)

  return (
    <Alert tone={isDenied(error) ? 'warning' : 'danger'} className={className}>
      <AlertTriangle aria-hidden />
      <div className="space-y-1">
        <AlertTitle>{error.title}</AlertTitle>
        <AlertDescription>{resolveErrorMessage(error)}</AlertDescription>
        {fieldErrors.length > 0 ? (
          <ul className="list-inside list-disc space-y-0.5 text-xs text-body">
            {fieldErrors.map((message) => (
              <li key={message}>{message}</li>
            ))}
          </ul>
        ) : null}
        <ErrorReference error={error} />
      </div>
    </Alert>
  )
}

function ErrorReference({ error }: { error: ApiError }) {
  if (!error.code && !error.correlationId) {
    return null
  }

  return (
    <p className="font-mono text-2xs text-muted">
      {error.code ? <span>{error.code}</span> : null}
      {error.code && error.correlationId ? <span aria-hidden> · </span> : null}
      {error.correlationId ? <span>ref {error.correlationId}</span> : null}
    </p>
  )
}
